import { useAuth0 } from '@auth0/auth0-react';
import { useState } from 'react';
import { z } from 'zod';

import { API_URL, apiFetch } from '@/config/api';

const uploadSchema = z
  .object({
    source_votes: z
      .record(z.string().uuid(), z.number().int().positive())
      .refine((votes) => {
        const sources = Object.keys(votes).length;
        return sources >= 1 && sources <= 100;
      }),
    content: z.string(),
  })
  .strict();

const resultSchema = z.object({
  upload_id: z.string().regex(/^[0-9a-f]{64}$/),
  status: z.enum(['created', 'existing']),
});

type UploadResult = z.infer<typeof resultSchema>;
type UploadEntry = {
  file: File;
  status: 'pending' | 'uploading' | 'done' | 'error';
  result?: UploadResult;
  error?: string;
};

async function readUpload(file: File) {
  // A 10 MiB file grows to roughly 14 MiB when wrapped as base64 JSON
  if (file.size > 15 * 1024 * 1024) {
    throw new Error('File too large. Each case can contain at most 10 MiB.');
  }
  let input: unknown;
  try {
    input = JSON.parse(await file.text());
  } catch {
    throw new Error('Select a JSON upload file.');
  }
  const parsed = uploadSchema.safeParse(input);
  if (!parsed.success) {
    throw new Error(
      'Expected source_votes and base64 content. Make upload files with the prepare command.'
    );
  }
  return parsed.data;
}

export function useEvalCaseUploads() {
  const { getAccessTokenSilently } = useAuth0();
  const [entries, setEntries] = useState<UploadEntry[]>([]);
  const [busy, setBusy] = useState(false);

  function selectFiles(files: FileList | null) {
    if (busy) return;
    // A selected folder can also hold metadata files such as .DS_Store or macOS ._M2.json, which would never upload
    const uploadFiles = Array.from(files ?? []).filter(
      (file) =>
        file.name.toLowerCase().endsWith('.json') && !file.name.startsWith('.')
    );
    // Replace the list even when nothing matches, so an earlier selection is never uploaded by mistake
    setEntries(uploadFiles.map((file) => ({ file, status: 'pending' })));
  }

  async function upload() {
    if (busy) return;
    setBusy(true);
    const update = (index: number, patch: Partial<UploadEntry>) =>
      setEntries((current) =>
        current.map((entry, i) =>
          i === index ? { ...entry, ...patch } : entry
        )
      );
    try {
      // Upload one at a time to avoid keeping a whole folder of private payloads in memory
      for (const [index, entry] of entries.entries()) {
        if (entry.status === 'done') continue;
        update(index, { status: 'uploading', error: undefined });
        try {
          const body = await readUpload(entry.file);
          const token = await getAccessTokenSilently();
          const response = await apiFetch(`${API_URL}/admin/eval-cases`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
            signal: AbortSignal.timeout(120_000),
          });
          let data: unknown;
          try {
            data = await response.json();
          } catch {
            throw new Error(
              `Unexpected server response (HTTP ${response.status}). Retry to confirm whether it was uploaded.`
            );
          }
          if (!response.ok) {
            // Validation errors can echo private input; show only the server's deliberate public message
            const error = z
              .object({ detail: z.object({ user_error_message: z.string() }) })
              .safeParse(data);
            throw new Error(
              error.success
                ? error.data.detail.user_error_message
                : `Upload failed (HTTP ${response.status}). Check your BTS access and the upload file.`
            );
          }
          const result = resultSchema.safeParse(data);
          if (!result.success)
            throw new Error(
              'Unexpected upload response. Retry to confirm whether it was uploaded.'
            );
          update(index, { status: 'done', result: result.data });
        } catch (error) {
          update(index, {
            status: 'error',
            error:
              error instanceof Error
                ? error.message
                : 'Upload failed. Try again.',
          });
        }
      }
    } finally {
      setBusy(false);
    }
  }

  return { entries, busy, selectFiles, upload };
}
